import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import {of as observableOf,  Observable } from 'rxjs';
import {catchError} from 'rxjs/operators';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/response';
import { ActivityFilter, IContactHistories, IActivityLogs, IAddContact } from './contacts-model';

@Injectable()
export class ContactsService {
    constructor(
        private http: HttpClient
    ) { }

    private static get URL(): string { return `${environment.dashboardApi.baseUrl}${environment.dashboardApi.prefix}`; }
    private static readonly ENDPOINT = 'contacts/{contactId}';
    /** GET */
    public getContactHistory(contactId: string): Observable<SuccessResponse<IContactHistories> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IContactHistories>>(
                ContactsService.URL +
                ContactsService.ENDPOINT.replace('{contactId}', contactId) +
                '/histories'
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getActivityLogs(activityFilter: ActivityFilter): Observable<SuccessResponse<IActivityLogs> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IActivityLogs>>(
                ContactsService.URL +
                'contacts/activities', activityFilter)
            .pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getFilteredContacts(filter: any): Observable<SuccessResponse<any> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IActivityLogs>>(
                ContactsService.URL +
                'contacts/filters', filter)
            .pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public restoreContacts(contactIds: string[]):
    Observable<SuccessResponse<boolean> | ErrorResponse>{
        return this.http
            .put<SuccessResponse<boolean>>(
                ContactsService.URL +
                'contacts/restores', { contactIds }
            )
            .pipe(catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** Moves contacts to the trash (soft delete); they can be restored during the retention period. */
    public softDeleteContacts(contactIds: string[]):
    Observable<SuccessResponse<boolean> | ErrorResponse>{
        return this.http
            .put<SuccessResponse<boolean>>(
                ContactsService.URL +
                'contacts/batchsoftdelete', { ids: contactIds, isSelectAll: false }
            )
            .pipe(catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** Permanently deletes (anonimizes) trashed contacts. Cannot be undone. */
    public anonimizeContacts(contactIds: string[]):
    Observable<SuccessResponse<boolean> | ErrorResponse>{
        return this.http
            .put<SuccessResponse<boolean>>(
                ContactsService.URL +
                'contacts/anonimizebatch', { ids: contactIds, isSelectAll: false }
            )
            .pipe(catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** POST */
    public addContact(contact: IAddContact):
    Observable<SuccessResponse<boolean> | ErrorResponse>{
        return this.http
            .post<SuccessResponse<boolean>>(
                ContactsService.URL +
                'contacts', contact
            )
            .pipe(catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }
}
